import React, { useEffect, useRef, useState } from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import modalContainer from '../modalContainer/modalContainer'
import { RadioWrapper } from '../radioButton'
import CustomTextarea from '../CustomTextarea'
import DatePicker from '../datePicker'
import {
  createBanner as createBannerAction,
  fetchFutureBanners as fetchFutureBannersAction,
  updateBanner as updateBannerAction,
} from '../../store/actions/banner'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import TimePicker from '../timePicker'
import styled from 'styled-components'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import { validateBanner } from './BannerValidator'
import HelpChevron from '../helpChevron'
import colors from '../styles/iaColors'
import { ErrorSection, ErrorWrapper } from '../styles/iaLayout'
import IaAlert, { alertType } from '../alert/Alert'
import { getErrorMessage, getFutureBanners } from '../../store/reducers/banner'
import AppConstants from '../../AppConstants'

const StyledBody = styled(ModalBody)`
  h5 {
    padding: 12px 0 4px;
    &:first-of-type {
      padding: 4px 0;
    }
  }
`

const FlexDiv = styled.div`
  display: flex;
  margin-bottom: 8px;
  > span {
    flex: 0 0 150px;
    &:first-of-type {
      flex: 0 0 50px;
    }
  }
`

const ValidationMessage = styled.div`
  color: ${colors.IA_COLOR_16};
`

const initialBanner = {
  application: undefined,
  message: '',
  displayFrom: undefined,
  displayTo: undefined,
  displayFromTime: undefined,
  displayToTime: undefined,
  priority: undefined,
}

const tjanstButtons = Object.entries(AppConstants.service).map(([key, value]) => {
  return {
    id: `tjanst${key}`,
    label: value,
    value: key,
  }
})

const prioButtons = Object.entries(AppConstants.prio).map(([key, value]) => {
  return {
    id: `prio${key}`,
    label: value,
    value: key,
  }
})

const CreateBanner = ({ handleClose, isOpen, onComplete, data }) => {
  const dispatch = useAppDispatch()
  const futureBanners = useAppSelector(getFutureBanners)
  const errorMessage = useAppSelector(getErrorMessage)
  const [validationMessages, setValidationMessages] = useState({})
  const [update, setUpdate] = useState(false)
  const [newBanner, setNewBanner] = useState(initialBanner)
  const [errorActive, setErrorActive] = useState(false)
  const [serverErrorActive, setServerErrorActive] = useState(false)

  const setApplicationAndCheckFuture = (application) => {
    dispatch(fetchFutureBannersAction(application)).finally(() => {
      setNewBanner({ ...newBanner, application })
    })
  }

  useEffect(() => {
    if (data && data.banner) {
      setUpdate(true)
      let displayFrom = new Date(data.banner.displayFrom)
      let displayTo = new Date(data.banner.displayTo)
      setNewBanner({
        ...data.banner,
        displayFrom: displayFrom.toLocaleDateString('sv-SE').replace(/[^ -~]/g, ''),
        displayTo: displayTo.toLocaleDateString('sv-SE').replace(/[^ -~]/g, ''),
        displayFromTime: displayFrom.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }).replace(/[^ -~]/g, ''),
        displayToTime: displayTo.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }).replace(/[^ -~]/g, ''),
      })
    } else {
      setUpdate(false)
    }
  }, [data])

  useEffect(() => {
    if (isEqual(previousBanner.current, newBanner)) {
      return
    }
    setValidationMessages(validateBanner(newBanner, futureBanners))
  }, [newBanner, futureBanners])

  //Show correct message for server induced errors
  useEffect(() => {
    if (!serverErrorActive) {
      return
    }
    if (errorMessage != null && 'ALREADY_EXISTS' === errorMessage.code) {
      const message = 'Ändra visningsperioden. Det finns redan en driftbanner som infaller i denna period.'
      setValidationMessages({ displayTo: message, displayFrom: '', displayToTime: '', displayFromTime: '' })
    } else {
      setErrorActive(true)
    }
  }, [serverErrorActive, errorMessage])

  const previousBanner = useRef()
  useEffect(() => {
    previousBanner.current = newBanner
  })

  const onChange = (value, prop) => {
    setNewBanner({ ...newBanner, [prop]: value })
  }

  const createSendObject = () => {
    return {
      application: newBanner.application,
      message: newBanner.message,
      displayFrom: newBanner.displayFrom + 'T' + newBanner.displayFromTime + ':00',
      displayTo: newBanner.displayTo + 'T' + newBanner.displayToTime + ':00',
      priority: newBanner.priority,
    }
  }

  const send = () => {
    const func = update
      ? dispatch(updateBannerAction(createSendObject(), data.banner.id))
      : dispatch(createBannerAction(createSendObject()))

    func
      .then(() => {
        cancel()
        onComplete()
      })
      .catch((_data) => {
        setServerErrorActive(true)
      })
  }

  const cancel = () => {
    setErrorActive(false)
    setServerErrorActive(false)
    setNewBanner(initialBanner)
    handleClose()
  }

  const enableSaveBtn = () => {
    const fields = ['application', 'message', 'displayFrom', 'displayTo', 'priority']
    const fieldsEdit = fields.concat(['displayFromTime', 'displayToTime'])

    let enable = fieldsEdit.reduce((accumulator, currentValue) => {
      return accumulator && newBanner[currentValue]
    }, true)

    enable = enable && isEmpty(validationMessages)

    if (update) {
      let changed = fields.reduce((accumulator, currentValue) => {
        let compareValue = newBanner[currentValue]
        if (currentValue === 'displayFrom') {
          compareValue = newBanner[currentValue] + 'T' + newBanner['displayFromTime'] + ':00'
        } else if (currentValue === 'displayTo') {
          compareValue = newBanner[currentValue] + 'T' + newBanner['displayToTime'] + ':00'
        }

        return accumulator || (data && data.banner && data.banner[currentValue] !== compareValue)
      }, false)

      enable = enable && changed
    }

    return enable
  }

  return (
    <Modal isOpen={isOpen} size={'md'} backdrop={true} toggle={cancel}>
      <ModalHeader toggle={cancel}>{update ? 'Ändra driftbannerns innehåll' : 'Skapa driftbanner'}</ModalHeader>
      <StyledBody>
        <h5>Välj tjänst</h5>
        <RadioWrapper
          radioButtons={tjanstButtons}
          onChange={(event) => setApplicationAndCheckFuture(event.target.value)}
          selected={newBanner.application}
        />
        <h5>Skriv meddelandetext</h5>
        <CustomTextarea
          inputId="bannerMessage"
          className="show-external-link"
          onChange={(value) => onChange(value, 'message')}
          value={newBanner.message}
          limit={200}
        />
        <h5>Ange visningsperiod</h5>
        <FlexDiv>
          <span>Från</span>
          <span>
            <DatePicker
              inputId="displayFromDate"
              date={newBanner.displayFrom}
              onChange={(value) => onChange(value, 'displayFrom')}
              className={validationMessages.displayFrom !== undefined ? 'error' : ''}
            />
          </span>
          <span>
            <TimePicker
              inputId="displayFromTime"
              value={newBanner.displayFromTime}
              onChange={(value) => onChange(value, 'displayFromTime')}
              className={validationMessages.displayFromTime !== undefined ? 'error' : ''}
            />
          </span>
        </FlexDiv>
        <ValidationMessage>{validationMessages.displayFrom}</ValidationMessage>
        <ValidationMessage>{validationMessages.displayFromTime}</ValidationMessage>
        <FlexDiv>
          <span>till</span>
          <span>
            <DatePicker
              inputId="displayToDate"
              date={newBanner.displayTo}
              onChange={(value) => onChange(value, 'displayTo')}
              className={validationMessages.displayTo !== undefined ? 'error' : ''}
            />
          </span>
          <span>
            <TimePicker
              inputId="displayToTime"
              value={newBanner.displayToTime}
              onChange={(value) => onChange(value, 'displayToTime')}
              className={validationMessages.displayToTime !== undefined ? 'error' : ''}
            />
          </span>
        </FlexDiv>
        <ValidationMessage>{validationMessages.displayTo}</ValidationMessage>
        <ValidationMessage>{validationMessages.displayToTime}</ValidationMessage>
        <HelpChevron label={'Välj prioritet'}>
          <h5>Låg prioritet</h5>
          <p>Används för information som inte påverkar användandet av tjänsten. En blå driftbanner visas.</p>
          <h5>Medel prioritet</h5>
          <p>
            Används för information som användaren bör uppmärksamma och för händelser som inte påverkar användandet av tjänsten, till
            exempel vid kommande driftstörningar. En gul driftbanner visas.
          </p>
          <h5>Hög prioritet</h5>
          <p>
            Används för information som användaren behöver uppmärksamma och för händelser som påverkar användandet av tjänsten, till exempel
            vid pågående driftstörningar. En röd driftbanner visas.
          </p>
        </HelpChevron>
        <RadioWrapper
          radioButtons={prioButtons}
          onChange={(event) => onChange(event.target.value, 'priority')}
          selected={newBanner.priority}
        />
      </StyledBody>
      <ErrorSection>
        {errorActive && (
          <ErrorWrapper>
            <IaAlert type={alertType.ERROR}>
              Driftbannern kunde inte {update ? 'ändras' : 'skapas'} på grund av ett tekniskt fel. Prova igen om en stund.
            </IaAlert>
          </ErrorWrapper>
        )}
      </ErrorSection>
      <ModalFooter className="no-border">
        <Button
          id="saveBanner"
          disabled={!enableSaveBtn()}
          color={'primary'}
          onClick={() => {
            send()
          }}>
          {update ? 'Ändra' : 'Skapa'}
        </Button>
        <Button
          id="closeBanner"
          color={'default'}
          onClick={() => {
            cancel()
          }}>
          Avbryt
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export const CreateBannerId = 'createBanner'

export default modalContainer(CreateBannerId)(CreateBanner)
